/**
 * GELD — Geld & Financieel rulebook.
 *
 * Sourced from DEFAULT_POLICY (Geld entries) +
 * docs/audits/DIDACTISCH_AUDIT_GELD_2026.md + MODULE_AUTHORING_GUIDE §5.
 */

import type { ModuleRulebook } from './types';
export const GELD_RULEBOOK: ModuleRulebook = {
  moduleId: 'Geld',
  shortCode: 'GELD',
  displayName: 'Geld',
  didactics: {
    principles: ['euro_cent_onderscheid', 'wisselgeld_logica', 'procent_in_context', 'budget_planning'],
    schoolbookReferences: ['Wereld in Getallen', 'Pluspunt'],
    misconceptionDomain: 'geld',
    coreSummary: 'Geld in contextopgaven: tellen, wisselen, terugrekenen, korting, BTW, budget en eenvoudige rente.'
  },
  notation: {
    requireCommaDecimals: true,
    requireEuroComma: true,
    forbiddenPatterns: [{
      pattern: '€\\d+\\.\\d{2}',
      label: 'Euro-bedrag met punt (gebruik €12,50)'
    }]
  },
  content: {
    distractorMisconceptionPattern: 'euro|cent|wisselgeld|korting|btw|budget|rente'
  },
  explanation: {
    requireRuleReference: true,
    checkStrategyMinTier: null,
    preferredStrategyKeywords: ['wisselgeld', 'korting', 'btw', 'percentage', 'budget']
  },
  visuals: {
    preferredAidTypes: ['money', 'receipt', 'budgetBar', 'priceCompare', 'barModel'],
    familyVisualHints: [{
      familyIdPattern: '^GELD-(TELLEN|MAAK|WISSEL|WISSELEN)',
      preferredStimulus: 'money',
      autoExemptEmbedded: true,
      note: 'Geld-tellen / wisselen vraagt om munten/biljetten visueel. autoExemptEmbedded dekt het money-vraagtype: de renderer IS de munten/biljetten-visual.'
    }, {
      familyIdPattern: '^GELD-TERUG(REKENEN)?',
      preferredStimulus: 'money',
      preferredSupport: 'receipt',
      autoExemptEmbedded: true,
      note: 'Wisselgeld terugrekenen: betaald bedrag in munten + bonnetje als context. autoExemptEmbedded dekt money / step_solver met eigen visueel oppervlak.'
    }, {
      familyIdPattern: '^GELD-(BOODSCHAPPEN|KOOPJES|PRIJS-PER)',
      preferredStimulus: 'receipt',
      autoExemptEmbedded: true,
      note: 'Bonnetjes voor boodschappen-context. autoExemptEmbedded dekt step_solver / bar_model_part met eigen visueel oppervlak.'
    }, {
      familyIdPattern: '^GELD-(BUDGET|SPAREN)',
      preferredSupport: 'budgetBar',
      note: 'Budget-balk om verdeling visueel te maken.'
    }, {
      familyIdPattern: '^GELD-KORTING',
      preferredSupport: 'barModel',
      supportAllowed: ['barModel', 'rule', 'percentageBar', 'priceCompare'],
      note: 'Korting: strookmodel toont oorspronkelijke prijs als geheel en korting als deel. Rule-kaart en priceCompare ook didactisch valide.'
    }, {
      familyIdPattern: '^GELD-BTW',
      preferredStimulus: 'percentageBar',
      preferredSupport: 'rule',
      supportAllowed: ['rule', 'percentageBar', 'barModel'],
      autoExemptEmbedded: true,
      note: 'BTW: percentageBar als stimulus (21% / 9% naast €-bedrag). Support: rule-kaart draagt de ×1,21 / ÷1,21-methode (handig didactisch script). 100:21 past niet in barModel-segment-cap. autoExemptEmbedded dekt fill_in / step_solver met eigen visueel oppervlak.'
    }, {
      familyIdPattern: '^GELD-RENTE-PROC',
      preferredSupport: 'percentageBar',
      note: 'Rente als procent: percentageBar koppelt % aan euro-bedrag.'
    }, {
      familyIdPattern: '^GELD-KOMMA',
      preferredSupport: 'rule',
      roleScope: 'rule-only',
      note: 'Geld-notatie (€-komma-cent): regel-kaart als geheugensteun.'
    }]
  },
  questionTypes: {
    preferredTypes: ['multiple_choice', 'fill_in', 'money', 'estimation_slider', 'step_solver', 'bar_model_part'],
    customTypeRoles: [{
      type: 'money',
      didacticRole: 'visual',
      preferredDok: 1,
      appropriateTiers: {
        min: 1,
        max: 10
      },
      reason: 'Munten/biljetten visueel manipuleren (tellen, maken, wisselen, terugrekenen). Het signatuur-vraagtype van GELD — de renderer IS het didactische oppervlak. Lokt euro/cent-verwarring (geld.euro-cent-swap) uit zonder dat het kind een tekstuele bedrag-notatie hoeft te lezen.'
    }, {
      type: 'bar_model_part',
      didacticRole: 'strategic',
      preferredDok: 2,
      appropriateTiers: {
        min: 7,
        max: 14
      },
      reason: 'Strookmodel bij korting/BTW: het kind leest het deel-bedrag af i.p.v. te rekenen — bouwt procent-intuïtie.'
    }, {
      type: 'step_solver',
      didacticRole: 'strategic',
      preferredDok: 2,
      appropriateTiers: {
        min: 7,
        max: 14
      },
      reason: 'Meerstaps geld-context (terugrekenen, korting, BTW, rente): elke stap (basis → percentage → bedrag → som) krijgt eigen controlepunt. Voorkomt dat het kind één rekenstap "wegslikt" en de fout pas zichtbaar wordt in het eindbedrag.'
    }, {
      type: 'estimation_slider',
      didacticRole: 'concept',
      preferredDok: 1,
      appropriateTiers: {
        min: 2,
        max: 8
      },
      reason: 'Geld-schatten ("Hoeveel kost een gemiddelde fiets?") via slider: bouwt prijsgevoel als bescherming tegen onrealistische uitkomsten van eigen berekening. Tolerantie-band, geen exact antwoord.'
    }, {
      type: 'fill_in',
      didacticRole: 'fluency',
      preferredDok: 2,
      appropriateTiers: {
        min: 4,
        max: 12
      },
      reason: 'Exact bedrag invullen met euro-komma-cent notatie. De notatie zelf (€12,50, niet €12.50 en niet 1250) is leerdoel — fill_in maakt notatie-fouten direct zichtbaar in plaats van weggemoffeld in een MC-keuze.'
    }, {
      type: 'multiple_choice',
      didacticRole: 'recall',
      preferredDok: 1,
      appropriateTiers: {
        min: 1,
        max: 10
      },
      reason: 'Keuze uit bedrag-opties of strategie-stappen. Niet voor zuivere tel- of wisselvragen op tier 1-3: daar dwingt MC niet tot de munt-manipulatie die het leerdoel IS — gebruik money-renderer.'
    }],
    familyTypeHints: [{
      familyIdPattern: '^GELD-TELLEN',
      preferredTypes: ['money', 'fill_in', 'multiple_choice'],
      discouragedTypes: ['true_false_series'],
      note: 'Geld tellen: munten/biljetten visueel optellen — money-renderer of exact bedrag invullen.'
    }, {
      familyIdPattern: '^GELD-(MAAK|WISSEL|WISSELEN)',
      preferredTypes: ['money', 'fill_in', 'multiple_choice', 'step_solver'],
      discouragedTypes: ['true_false_series'],
      note: 'Geld maken/wisselen vraagt om actieve constructie of berekening — herkenning via TF is te oppervlakkig.'
    }, {
      familyIdPattern: '^GELD-(KORTING|BTW|RENTE-PROC|PRIJS-PER)',
      preferredTypes: ['step_solver', 'fill_in', 'bar_model_part', 'multiple_choice'],
      discouragedTypes: ['true_false_series'],
      note: 'Korting/BTW/rente/prijs-per: meerstaps procent-berekening; strookmodel of step_solver maakt strategie expliciet.'
    }, {
      familyIdPattern: '^GELD-(BUDGET|BOODSCHAPPEN|KOOPJES|SPAREN|TERUG|TERUGREKENEN)',
      preferredTypes: ['step_solver', 'fill_in', 'multiple_choice'],
      discouragedTypes: ['true_false_series'],
      note: 'Context-rekenen met geld: meerstaps in realistische scenario; step_solver houdt strategie expliciet.'
    }, {
      familyIdPattern: '^GELD-KOMMA',
      preferredTypes: ['fill_in', 'multiple_choice'],
      note: 'Geld-notatie (€-komma-cent): nauwkeurige typing/keuze, geen TF.'
    }]
  },
  distractorTraps: [{
    id: 'euro-cent-swap',
    label: 'Euro/cent verwisselen',
    description: '€2,50 als €250 of 250 cent als €25 interpreteren.'
  }, {
    id: 'wrong-discount-direction',
    label: 'Korting verkeerde richting',
    description: 'Korting OPTELLEN bij de prijs i.p.v. aftrekken.'
  }, {
    id: 'percentage-of-wrong-base',
    label: 'Percentage van verkeerde basis',
    description: 'BTW van bruto i.p.v. netto berekenen (of andersom).'
  }],
  keyRules: ['Euro-notatie: €12,50 (komma, niet punt)', 'Money-visual verplicht voor geld-telvragen', 'Trap-distractor: euro/cent verwisselen', 'BTW-tarief: 21% (actueel NL)', 'Wisselgeld altijd vanuit het betaalde bedrag terugrekenen', 'Strookmodel (barModel) bij korting/BTW-context — koppelt % en €'],
  // GSR-25 (PR-9): per-module override copy migrated from inline
  // `moduleMessages` maps in didacticDepth.ts.
  qualityMessages: {
    explanationDepthRequirement: 'GELD explanation must show actual calculation with euro/cent',
    distractorMisconceptionRequirement: 'GELD distractors should reference euro/cent notation error'
  },
  authoringGuideAnchor: '#5-geld--geld--financieel'
};